import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";

const rentalTypes = [
  { icon: "fas fa-city", title: "Apartments in central business districts" },
  { icon: "fas fa-house", title: "Family-friendly villas in quiet communities" },
  { icon: "fas fa-water", title: "Waterfront homes with scenic views" },
  { icon: "fas fa-coins", title: "Affordable units in emerging areas" },
];

const whyRent = [
  { icon: "fas fa-dumbbell", title: "They get access to world-class facilities and amenities." },
  { icon: "fas fa-house-chimney", title: "They offer flexible living options without long-term commitment." },
  { icon: "fas fa-road", title: "Easy connectivity to key areas of the city" },
];

const rentalSteps = [
  ["Understand Your Needs", "We identify your budget, preferred location, and property type."],
  ["Property Selection", "You receive a curated list of suitable options."],
  ["Viewings and Shortlisting", "We arrange visits so you can compare and decide."],
  ["Agreement and Move-In", "We assist with lease agreements and documentation."],
];

const faqs = [
  {
    icon: "fas fa-city",
    question: "What types of rental properties are available in Abu Dhabi?",
    answer: "In Abu Dhabi, you find all different types of properties for your needs, such as apartments, villas, townhouses, and serviced residences.",
  },
  {
    icon: "fas fa-handshake",
    question: "How do I choose the right rental property in Abu Dhabi?",
    answer: "It is very easy. Connect with us to tell us your requirements. Our team will listen to you carefully and then come up with the best options based on your preferences. You can explore them and make the right choice.",
  },
  {
    icon: "fas fa-id-card",
    question: "What documents are required to rent a property?",
    answer: "You must have a valid Emirates ID, passport copy, visa, and proof of income or employment. If you need a business property on rent, then you should also have the proper registration number.",
  },
  {
    icon: "fas fa-coins",
    question: "How much is the security deposit for rental properties?",
    answer: "The deposit is usually a percentage of the annual rent and is refundable at the end of the lease, subject to conditions.",
  },
  {
    icon: "fas fa-comments-dollar",
    question: "Can I negotiate rent in Abu Dhabi?",
    answer: "Yes. Of course. You can negotiate.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "Rental Property Abu Dhabi", item: "https://nvrealestate.ae/rental-property-abu-dhabi" },
  ],
};

function RentalPropertyAbuDhabi() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>Rental Property Abu Dhabi – Nvrealestate</title>
        <meta
          name="description"
          content="Searching for a rental property in Abu Dhabi? We are a trusted real estate company in Abu Dhabi dedicated to providing the best solutions to your needs. Let us connect now."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/rental-property-abu-dhabi/01.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi rental specialists</span>
              <h1 id="villa-page-title">Rental Property Abu Dhabi - Find a Place That Feels Right</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Rent&city=&developer=&developer_name=&project=&project_name=&property_type=&bedrooms=&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore properties
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="/images/seo/rental-property-abu-dhabi/02.png"
                alt="Rental properties in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Trusted realtors</strong>
                <span>Matching your rental needs to the right property</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Rental Property Abu Dhabi - Find a Place That Feels Right</h2>
              <p>
                Nova Vision Rental Estate helps individuals and business owners in Abu Dhabi to get
                the best rental property in Abu Dhabi. We have our connection with top developers in
                the city. When someone comes to us with their requirements for a residential or
                commercial rental property, our experts first listen to them carefully and then,
                based on their words, filter the database of available properties to recommend the
                best ones matching their needs. We have already helped so many people in the city to
                get a rental property for their purposes, and all of them are happy with our
                services. With our commitment to matching the expectations of our clients, we have
                obtained a place among the most reliable realtors in Abu Dhabi.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="explore-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Find your fit</span>
              <h2 id="explore-heading">Explore the Right Rental Property for You</h2>
              <p>
                Every renter has different priorities. Some focus on location, while others look
                for space, amenities, or budget-friendly options. With a wide selection of Rental
                property Abu Dhabi, you can choose from:
              </p>
            </div>
            <div className="villa-benefits__grid">
              {rentalTypes.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
            <p className="villa-process__closing">
              The key is finding a property that aligns with your daily routine and future plans.
            </p>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="why-rent-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Why rent here</span>
              <h2 id="why-rent-heading">Why Rent in Abu Dhabi?</h2>
              <p>
                Abu Dhabi continues to attract residents due to its quality of life, infrastructure,
                and safe environment. Renting here gives you flexibility while enjoying a modern
                lifestyle.
              </p>
              <p>Here are some reasons why many people choose a Rental property in Abu Dhabi:</p>
            </div>
            <div className="villa-benefits__grid">
              {whyRent.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
            <p className="villa-process__closing">
              Whether you are a professional, a family, or a newcomer, renting offers convenience
              and freedom.
            </p>
          </div>
        </section>

        <section className="villa-process villa-section" aria-labelledby="villa-process-title">
          <div className="container villa-process__layout">
            <div className="villa-process__aside">
              <span className="villa-kicker">The rental process</span>
              <h2 id="villa-process-title">A Simple Rental Process</h2>
              <p>
                Renting a property should be straightforward. With the right support, you can avoid
                delays and confusion. At NV Real Estate, the process is clear and efficient:
              </p>
              <Link className="villa-text-link" to="/contact-us">Speak with an advisor <span aria-hidden="true">-&gt;</span></Link>
            </div>
            <div className="villa-process__steps-wrap">
              <ol className="villa-process__steps">
                {rentalSteps.map(([title, text], index) => (
                  <li key={title}>
                    <span className="villa-process__number">{String(index + 1).padStart(2, "0")}</span>
                    <div><h3>{title}</h3><p>{text}</p></div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Why Trust NV Real Estate for a Rental Property in Abu Dhabi?</h2>
                <p>
                  Nova Vision Real Estate is a well-admired and trusted realtor in Abu Dhabi for
                  rental properties. Here are some of the top reasons that make us a reliable option
                  for people.
                </p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
                <p className="villa-advisor__closing">
                  So, don't waste any time and connect with us now to get the best property for your
                  needs.
                </p>
              </div>
              <ul>
                <li>We have a specialized team of real estate professionals.</li>
                <li>Our team is highly experienced and committed to the best service.</li>
                <li>We have a great connection with top developers in Abu Dhabi.</li>
                <li>We have already helped so many people to get rental properties.</li>
                <li>Most of our clients appreciate us.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Renter questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you begin your rental search with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`rental-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`rental-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-home" /><div><strong>Curated Homes</strong><span>Matched to Your Brief</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-map-marker-alt" /><div><strong>Local Knowledge</strong><span>Across Abu Dhabi</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Lease Support</strong><span>Agreement to Move-In</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">
              Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email us
              now at <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.
            </p>
          </div>
        </section>
      </main>

      <CTA />

      <style>{`
        .villa-benefits--centered-cards .villa-benefits__grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 24px;
        }
        .villa-benefits--centered-cards .villa-benefit-card {
          flex: 1 1 260px;
          max-width: 300px;
        }
        @media (max-width: 700px) {
          .villa-benefits--centered-cards .villa-benefit-card {
            flex: 1 1 100%;
            max-width: 420px;
          }
        }
      `}</style>
    </>
  );
}

export default RentalPropertyAbuDhabi;